import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import homeNewsAPI from '../utils/api/homeNewsAPI';
import { HOME_NEWS_CATEGORIES } from '../utils/homeNewsCategories';
import './AdminOtherNews.css';

const initialFormState = {
  title: '',
  description: '',
  content: '',
  image: '',
  featuredImage: null,
  author: 'Admin',
  location: '',
  category: HOME_NEWS_CATEGORIES[0],
  isPublished: true
};

function AdminHomeNewss() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState('');
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const toastTimerRef = useRef(null);

  const imagePreview = formData.featuredImage instanceof File
    ? URL.createObjectURL(formData.featuredImage)
    : formData.image || '';

  useEffect(() => {
    async function loadPageData() {
      try {
        setPageLoading(true);
        const response = await homeNewsAPI.getAdminNews({ limit: 200 });
        if (response.success) {
          setNewsList(response.data || []);
        }
      } catch (error) {
        showMessage('डेटा लोड नहीं हो सका', 'error');
      } finally {
        setPageLoading(false);
      }
    }
    loadPageData();
  }, []);

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setMessage(''), 3000);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const refreshNewsList = async () => {
    const response = await homeNewsAPI.getAdminNews({ limit: 200 });
    if (response.success) {
      setNewsList(response.data || []);
    }
  };

  const resetForm = () => {
    setEditingId('');
    setFormData(initialFormState);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setFormData((current) => ({
      ...current,
      featuredImage: file
    }));
  };

  const buildSubmitData = () => {
    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('description', formData.description);
    submitData.append('content', formData.content);
    submitData.append('author', formData.author);
    submitData.append('location', formData.location);
    submitData.append('category', formData.category);
    submitData.append('isPublished', String(formData.isPublished));
    submitData.append('image', formData.image || '');

    if (formData.featuredImage instanceof File) {
      submitData.append('featuredImage', formData.featuredImage);
    }

    return submitData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const submitData = buildSubmitData();
      const response = editingId
        ? await homeNewsAPI.updateNews(editingId, submitData)
        : await homeNewsAPI.addNews(submitData);

      if (!response.success) {
        showMessage(response.message || 'होम न्यूज़ सेव नहीं हो सका', 'error');
        return;
      }

      showMessage(editingId ? 'होम न्यूज़ सफलतापूर्वक अपडेट कर दिया गया' : 'होम न्यूज़ सफलतापूर्वक जोड़ दिया गया');
      resetForm();
      await refreshNewsList();
    } catch (error) {
      showMessage('होम न्यूज़ सेव नहीं हो सका', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      content: item.content || '',
      image: item.image || '',
      featuredImage: null,
      author: item.author || 'Admin',
      location: item.location || '',
      category: item.category || HOME_NEWS_CATEGORIES[0],
      isPublished: item.isPublished !== false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('क्या आप यह होम न्यूज़ हटाना चाहते हैं?');
    if (!confirmed) {
      return;
    }

    const response = await homeNewsAPI.deleteNews(id);
    if (response.success) {
      showMessage('होम न्यूज़ हटा दिया गया');
      await refreshNewsList();
      return;
    }

    showMessage(response.message || 'होम न्यूज़ हटाया नहीं जा सका', 'error');
  };

  return (
    <AdminShell
      eyebrow="Homepage Sections"
      title="Manage homeNewss"
      description="Create homepage stories section-wise, so the selected category goes directly into the matching homepage block."
      primaryAction={{ label: 'Refresh list', onClick: refreshNewsList }}
      secondaryAction={{ label: 'Dashboard', onClick: () => navigate('/admin/dashboard') }}
      meta={[
        { label: 'Sections', value: String(HOME_NEWS_CATEGORIES.length) },
        { label: 'Saved items', value: String(newsList.length) }
      ]}
    >
      <div className="admin-other-news-page">
        {message && <div className={`admin-message ${messageType}`}>{message}</div>}

        <div className="admin-other-news-grid">
          <section className="admin-card">
            <h2>{editingId ? 'होम न्यूज़ संपादित करें' : 'नया होम न्यूज़ जोड़ें'}</h2>
            <form onSubmit={handleSubmit} className="admin-other-news-form">
              <div className="form-row two-column">
                <label>
                  <span>Title</span>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} required />
                </label>
                <label>
                  <span>Author</span>
                  <input type="text" name="author" value={formData.author} onChange={handleInputChange} />
                </label>
              </div>

              <div className="form-row two-column">
                <label>
                  <span>Category</span>
                  <select name="category" value={formData.category} onChange={handleInputChange} required>
                    {HOME_NEWS_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Location</span>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="चित्रकूट"
                  />
                </label>
              </div>

              <label>
                <span>Description</span>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} required />
              </label>

              <label>
                <span>Content</span>
                <textarea name="content" value={formData.content} onChange={handleInputChange} rows={7} required />
              </label>

              <label>
                <span>Upload Image</span>
                <input
                  type="file"
                  name="featuredImage"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </label>

              {imagePreview ? (
                <label>
                  <span>Preview</span>
                  <img
                    src={imagePreview}
                    alt="Home news preview"
                    style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '12px' }}
                  />
                </label>
              ) : null}

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isPublished"
                  checked={formData.isPublished}
                  onChange={handleInputChange}
                />
                <span>Publish immediately</span>
              </label>

              <div className="admin-other-news-form-actions">
                <button type="submit" className="primary-button" disabled={loading || pageLoading}>
                  {loading ? 'Saving...' : editingId ? 'Update Home News' : 'Save Home News'}
                </button>
                {editingId ? (
                  <button type="button" className="secondary-button" onClick={resetForm}>
                    Cancel edit
                  </button>
                ) : null}
              </div>
            </form>
          </section>

          <section className="admin-card list-card">
            <div className="list-card-header">
              <div>
                <h2>Saved homeNewss</h2>
                <p>Homepage ke liye category-wise saved items yahan dikhte hain.</p>
              </div>
            </div>

            {pageLoading ? (
              <div className="admin-empty-state">Loading...</div>
            ) : newsList.length === 0 ? (
              <div className="admin-empty-state">अभी तक कोई होम न्यूज़ जोड़ा नहीं गया है।</div>
            ) : (
              <div className="admin-other-news-list">
                {newsList.map((item) => (
                  <article key={item._id} className="saved-news-item">
                    <div className="saved-news-copy">
                      <div className="saved-news-topline">
                        <span className="saved-news-chip">{item.category}</span>
                        <span>{item.isPublished ? 'Published' : 'Draft'}</span>
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <div className="saved-news-meta">
                        <span>{item.location || 'No location'}</span>
                        <span>{item.author || 'Admin'}</span>
                        <span>{item.views || 0} views</span>
                        <span>{item.shareCount || 0} shares</span>
                      </div>
                    </div>
                    <div className="saved-news-actions">
                      <button type="button" className="secondary-button" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="danger-button" onClick={() => handleDelete(item._id)}>
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </AdminShell>
  );
}

export default AdminHomeNewss;
